import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Box,
    CssBaseline,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Avatar,
    Menu,
    MenuItem,
    Divider
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard,
    Subscriptions,
    Person,
    AdminPanelSettings,
    Api,
    Logout,
    Cloud,
    Article as ArticleIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axiosWithAuth from '../../utils/axiosWithAuth';
import { isCASOnlySubscription } from '../../utils/subscriptionUtils';

const drawerWidth = 240;

const Layout = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const { user, logout } = useAuth();
    const [hasAWCSubscription, setHasAWCSubscription] = useState(false);
    const navigate = useNavigate();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleMenuItemClick = (path) => {
        navigate(path);
        setMobileOpen(false); // Close mobile drawer after navigation
    };

    useEffect(() => {
        const loadSubscriptions = async () => {
            try {
                const res = await axiosWithAuth.get('/subscriptions/my-subscriptions');
                const subs = Array.isArray(res.data) ? res.data : [];
                const anyAWC = subs.some(sub => sub.status === 'active' && !isCASOnlySubscription(sub));
                setHasAWCSubscription(anyAWC);
            } catch (e) {
                setHasAWCSubscription(false);
            }
        };

        // Only fetch for non-admins; admins always see AWC
        if (!(user?.role === 'admin' || user?.role === 'superadmin')) {
            loadSubscriptions();
        }
    }, [user?.role]);

    const menuItems = [
        {
            text: 'Dashboard',
            icon: <Dashboard />,
            path: '/dashboard',
        },
        {
            text: 'Subscriptions',
            icon: <Subscriptions />,
            path: '/subscriptions',
        },
        {
            text: 'Content Analysis (CAS)',
            icon: <ArticleIcon />,
            path: '/cas',
        },
        // Profile item removed from sidebar - use top-right avatar menu instead
    ];

    // Add admin menu items for admin users
    if (user?.role === 'admin' || user?.role === 'superadmin') {
        menuItems.splice(2, 0, { text: 'AWC Service', icon: <Cloud />, path: '/service-operations' });
        menuItems.push({ text: 'Admin', icon: <AdminPanelSettings />, path: '/admin' });
        menuItems.push({ text: 'CAS-Admin', icon: <Api />, path: '/cas-admin' });
    } else if (hasAWCSubscription) {
        menuItems.splice(2, 0, { text: 'AWC Service', icon: <Cloud />, path: '/service-operations' });
    }

    const drawer = (
        <div>
            <Toolbar>
                <Typography variant="h6" noWrap component="div">
                    Clean Platform
                </Typography>
            </Toolbar>
            <Divider />
            <List>
                {menuItems.map((item) => (
                    <ListItem
                        button={!item.disabled}
                        key={item.text}
                        onClick={item.disabled ? undefined : () => handleMenuItemClick(item.path)}
                        disabled={item.disabled}
                        sx={{
                            opacity: item.disabled ? 0.5 : 1,
                            cursor: item.disabled ? 'default' : 'pointer'
                        }}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                    </ListItem>
                ))}
            </List>
        </div>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        Clean Subscription Platform
                    </Typography>

                    <IconButton
                        onClick={handleMenuOpen}
                        sx={{ ml: 2 }}
                    >
                        <Avatar sx={{ width: 32, height: 32 }}>
                            {user?.first_name?.[0] || user?.email?.[0] || 'U'}
                        </Avatar>
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
                            <ListItemIcon>
                                <Person fontSize="small" />
                            </ListItemIcon>
                            Profile
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon>
                                <Logout fontSize="small" />
                            </ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                }}
            >
                <Toolbar />
                <Outlet />
            </Box>
        </Box>
    );
};

export default Layout; 